/**
 * useTableState — manages: search, sort, filters, group-by, column layout
 * (visibility/order/width/freeze), and saved views (persisted per-user in
 * LocalStorage + workspace via `/api/v1/workspaces/{ws}/table-views`).
 *
 * A "layout" is: { columns, sort, filters, group_by, search }.
 *
 * Priority when loading a table (LS is layered on top of the current base):
 *   1. Resolve server "base view" (user_default_id → ws_default_id → null)
 *   2. If LS.base_view_id === base    → apply LS.layout (user's tweaks on top)
 *   3. Else if base view exists       → apply that view config (server wins,
 *                                        LS is reset so an admin's newly-set
 *                                        workspace default reaches every user)
 *   4. Else if LS exists (base null)  → apply LS.layout (pure ad-hoc tweaks)
 *   5. Else                            → `defaultLayout` prop (codebase fallback)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

function lsKey(ws, tk, uid) { return `acquilix.tableLayout.${ws}.${tk}.${uid}`; }

function readLS(ws, tk, uid) {
  try {
    const raw = localStorage.getItem(lsKey(ws, tk, uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Back-compat: older versions stored the layout directly.
    if (parsed && typeof parsed === "object" && parsed.layout) return parsed;
    return { layout: parsed, base_view_id: null };
  } catch { return null; }
}

function mergeColumns(defaultCols, layoutCols) {
  // Preserve any new columns added to the codebase (in default) but keep the
  // user's saved ordering / visibility / width / freeze for known ones.
  // Iter · Feb 2026 — Defensive de-dupe: if a codebase mistake ever ships
  // two column defs with the same `key`, or a saved layout carries dupes
  // from an older buggy build, keep only the FIRST occurrence. Duplicate
  // keys otherwise cause React key collisions + @dnd-kit sortable id
  // conflicts, which manifests as "several columns rename to <label>" when
  // the user drags to reorder.
  const seenDefault = new Set();
  const uniqueDefaults = (defaultCols || []).filter((c) => {
    if (!c || !c.key || seenDefault.has(c.key)) return false;
    seenDefault.add(c.key);
    return true;
  });
  if (!Array.isArray(layoutCols) || layoutCols.length === 0) return uniqueDefaults;
  const byKey = new Map();
  for (const c of layoutCols) {
    if (c && c.key && !byKey.has(c.key)) byKey.set(c.key, c);
  }
  const merged = uniqueDefaults.map((c, i) => {
    const saved = byKey.get(c.key);
    return {
      ...c,
      visible: saved?.visible ?? c.visible ?? true,
      order: saved?.order ?? i,
      width: saved?.width ?? c.width,
      frozen: saved?.frozen ?? c.frozen ?? false,
    };
  });
  merged.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  // Iter · Feb 2026 — Honour `pinAfter` so a column def can declare
  // "I must sit immediately to the right of column X" regardless of
  // what a user's stale saved layout did. Used e.g. by Bills →
  // Sub-category to stay adjacent to Category after we introduced the
  // taxonomy columns (users who had reordered pre-taxonomy ended up
  // with Amount wedged in between). Walk in declaration order so a
  // chain of pinAfter follows the originally defined sequence.
  const pinned = [];
  for (const c of uniqueDefaults) {
    if (!c.pinAfter) continue;
    const anchorKey = c.pinAfter;
    const idx = merged.findIndex((m) => m.key === c.key);
    const anchorIdx = merged.findIndex((m) => m.key === anchorKey);
    if (idx === -1 || anchorIdx === -1) continue;
    // Only move if not already adjacent (idx === anchorIdx + 1).
    if (idx === anchorIdx + 1) continue;
    const [row] = merged.splice(idx, 1);
    const newAnchorIdx = merged.findIndex((m) => m.key === anchorKey);
    merged.splice(newAnchorIdx + 1, 0, row);
    pinned.push(c.key);
  }
  merged.forEach((c, i) => { c.order = i; });
  return merged;
}

export default function useTableState({ tableKey, defaultColumns, defaultLayout = {}, extraLayoutState = null, onApplyExtraLayoutState = null }) {
  const { activeWorkspaceId, user } = useAuth();
  const uid = user?.id;

  // Views state (from backend)
  const [views, setViews] = useState([]);
  const [wsDefaultId, setWsDefaultId] = useState(null);
  const [userDefaultId, setUserDefaultId] = useState(null);
  const [activeViewId, setActiveViewId] = useState(null);
  // The server-resolved "base" view that LS tweaks are layered on top of.
  // When the server's base changes (e.g., admin sets a new workspace default,
  // or the user pins/unpins their own default), we discard stale LS.
  const [baseViewId, setBaseViewId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [viewsLoaded, setViewsLoaded] = useState(false);

  // Layout state (what's actually applied)
  const [columns, setColumns] = useState(() => mergeColumns(defaultColumns, defaultLayout.columns || []));
  // Feb 2026 · Portalwiz Roles fix — when the parent's `defaultColumns`
  // identity changes (e.g. UserManagement's `columns` useMemo re-runs
  // after `roleOptions` hydrates from 5 fallback slugs to 22 real ones),
  // the render closures on those column defs now reference the updated
  // state. Without this sync effect, the FIRST-mount snapshot keeps
  // rendering stale cells — which is exactly why every custom role
  // was vanishing from the User Management dropdown even though the
  // network response contained them.
  //
  // We merge with the CURRENT layout (visibility / order / width /
  // freeze) so saved views + user tweaks are preserved.
  const lastDefaultsRef = useRef(defaultColumns);
  useEffect(() => {
    if (lastDefaultsRef.current === defaultColumns) return;
    lastDefaultsRef.current = defaultColumns;
    setColumns((prev) => {
      const layoutCols = (prev || []).map((c) => ({
        key: c.key,
        visible: c.visible,
        order: c.order,
        width: c.width,
        frozen: c.frozen,
      }));
      return mergeColumns(defaultColumns, layoutCols);
    });
  }, [defaultColumns]);
  const [sort, setSort] = useState(() => defaultLayout.sort || []);
  const [filters, setFilters] = useState(() => defaultLayout.filters || []);
  const [groupBy, setGroupBy] = useState(defaultLayout.group_by || null);
  const [search, setSearch] = useState(defaultLayout.search || "");
  // Iter 247 · Row-wrap mode. `crop` (default) truncates via ellipsis,
  // `wrap` allows content to flow onto multiple lines, `nowrap` keeps every
  // cell on one line but doesn't add an ellipsis (lets the horizontal scroll
  // do the work).
  const [wrapMode, setWrapMode] = useState(defaultLayout.wrap_mode || "crop");
  const [dirty, setDirty] = useState(false);
  // Guard: only persist-to-LS AFTER loadViews (or the LS read) has hydrated state.
  // Prevents the mount-time persist effect from clobbering cached layouts before
  // we've had a chance to read them.
  const [hydrated, setHydrated] = useState(false);

  const currentLayout = useMemo(() => ({
    columns: columns.map((c) => ({
      key: c.key,
      visible: c.visible !== false,
      order: c.order,
      width: c.width || null,
      frozen: !!c.frozen,
    })),
    sort, filters, group_by: groupBy, search, wrap_mode: wrapMode,
    // Iter · Feb 2026 — Include page-level filter state (pill filters,
    // quick-report tabs, etc.) so "Save view" captures the ENTIRE UI
    // state a PM would want to restore, not just the AdvancedTable's
    // own columns/sort/filters.
    ...(extraLayoutState ? { extra_state: extraLayoutState } : {}),
  }), [columns, sort, filters, groupBy, search, wrapMode, extraLayoutState]);

  // Persist to LocalStorage on any change — but only after hydration.
  useEffect(() => {
    if (!hydrated || !activeWorkspaceId || !uid) return;
    try {
      localStorage.setItem(
        lsKey(activeWorkspaceId, tableKey, uid),
        JSON.stringify({ layout: currentLayout, base_view_id: baseViewId }),
      );
    } catch { /* quota */ }
  }, [currentLayout, activeWorkspaceId, uid, tableKey, hydrated, baseViewId]);

  // Feb 2026 · Cross-device sync of STRUCTURAL column prefs (widths,
  // order, visibility, frozen). Debounced 1s. Filters / sort / search
  // stay local (session-scoped). Prefs sit alongside LocalStorage;
  // they hydrate on mount (in loadViewsRef) BEFORE LS so the server
  // version wins when a fresh device comes online.
  const serverPrefsSaveTimer = useRef(null);
  useEffect(() => {
    if (!hydrated || !activeWorkspaceId || !uid) return;
    if (serverPrefsSaveTimer.current) clearTimeout(serverPrefsSaveTimer.current);
    serverPrefsSaveTimer.current = setTimeout(async () => {
      try {
        const cols = columns.map((c) => ({
          key: c.key,
          order: c.order,
          visible: c.visible !== false,
          width: c.width || null,
          frozen: !!c.frozen,
        }));
        await api.put(
          `/workspaces/${activeWorkspaceId}/table-prefs/${encodeURIComponent(tableKey)}`,
          { columns: cols },
        );
      } catch { /* network / 403 — LS still has the state */ }
    }, 1000);
    return () => { if (serverPrefsSaveTimer.current) clearTimeout(serverPrefsSaveTimer.current); };
    // Only re-fire when the STRUCTURAL slice changes — filters / sort /
    // search churn shouldn't hit the server.
     
  }, [columns, activeWorkspaceId, uid, tableKey, hydrated]);

  const applyLayout = useCallback((layout) => {
    if (!layout) return;
    setColumns(mergeColumns(defaultColumns, layout.columns || []));
    setSort(layout.sort || []);
    setFilters(layout.filters || []);
    setGroupBy(layout.group_by || null);
    setSearch(layout.search || "");
    setWrapMode(layout.wrap_mode || "crop");
    setDirty(false);
    // Iter · Feb 2026 — Propagate page-level extra state so parent
    // components (e.g. Tasks.jsx pill filters) can rehydrate too.
    if (layout.extra_state && typeof onApplyExtraLayoutState === "function") {
      try { onApplyExtraLayoutState(layout.extra_state); } catch { /* ignore */ }
    }
  }, [defaultColumns, onApplyExtraLayoutState]);

  // Iter · Feb 2026 — Keep the loadViews implementation in a ref so
  // useEffect can depend on STABLE identity (ws+tableKey+uid) instead
  // of the callback. Previously any parent re-render that changed the
  // `onApplyExtraLayoutState` prop identity (an inline arrow) would
  // regenerate applyLayout → loadViews → refire the effect → refetch
  // `/table-views` in a tight loop that hit 429 Too Many Requests
  // within seconds. The ref pattern breaks the dep chain.
  const loadViewsRef = useRef(null);
  loadViewsRef.current = async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/table-views`, {
        params: { table_key: tableKey },
      });
      setViews(data.views || []);
      setWsDefaultId(data.workspace_default_id || null);
      setUserDefaultId(data.user_default_id || null);
      setActiveViewId(data.active_view_id || null);
      setIsAdmin(!!data.is_admin);
      setViewsLoaded(true);

      // Resolve base view (user default → workspace default → null).
      const base = data.active_view_id || null;
      setBaseViewId(base);
      if (!uid) { setHydrated(true); return; }

      const ls = readLS(activeWorkspaceId, tableKey, uid);
      const baseView = base ? (data.views || []).find((x) => x.id === base) : null;

      if (ls && ls.base_view_id === base) {
        // LS is tweaks on top of the current base — keep them.
        applyLayout(ls.layout);
      } else if (baseView?.config) {
        // Base changed (admin pushed a new ws default, or user (un)pinned).
        // Discard stale LS and apply the server view.
        applyLayout(baseView.config);
      } else if (!base && ls) {
        // No server default; LS holds pure ad-hoc tweaks.
        applyLayout(ls.layout);
      }
      // else: keep the codebase defaultLayout that was seeded at init.

      // Feb 2026 · Overlay server-side structural prefs on top of
      // whatever LS/view seeded. Fresh devices with empty LS still get
      // the user's ambient column widths / order / visibility / frozen
      // state. Silently no-ops if the server doesn't know this table.
      try {
        const { data: pdata } = await api.get(
          `/workspaces/${activeWorkspaceId}/table-prefs/${encodeURIComponent(tableKey)}`,
        );
        const serverCols = pdata?.prefs?.columns;
        if (Array.isArray(serverCols) && serverCols.length) {
          setColumns((prev) => mergeColumns(defaultColumns, serverCols.map((sc) => {
            const cur = (prev || []).find((c) => c.key === sc.key) || {};
            return {
              key: sc.key,
              order: sc.order ?? cur.order,
              visible: sc.visible ?? (cur.visible !== false),
              width: sc.width ?? cur.width,
              frozen: sc.frozen ?? cur.frozen,
            };
          })));
        }
      } catch { /* silent — LS keeps working offline */ }
    } catch (e) {
      console.warn("table-views load failed", e);
    } finally {
      // Regardless of success/failure, mark hydrated so we start persisting.
      setHydrated(true);
    }
  };
  // Stable public reload for external callers (Save/Delete view actions).
  const loadViews = useCallback(() => loadViewsRef.current?.(), []);

  useEffect(() => {
    loadViewsRef.current?.();
    // ONLY re-fetch when the ownership triple actually changes.
  }, [activeWorkspaceId, tableKey, uid]);

  // Actions ────────────────────────────────────────────────────────────

  const setColumnVisibility = (key, visible) => {
    setColumns((cs) => cs.map((c) => c.key === key ? { ...c, visible } : c));
    setDirty(true);
  };
  const toggleFrozen = (key) => {
    setColumns((cs) => cs.map((c) => c.key === key ? { ...c, frozen: !c.frozen } : c));
    setDirty(true);
  };
  const reorderColumns = (fromKey, toKey) => {
    setColumns((cs) => {
      const from = cs.findIndex((c) => c.key === fromKey);
      const to = cs.findIndex((c) => c.key === toKey);
      if (from < 0 || to < 0) return cs;
      const arr = [...cs];
      const [it] = arr.splice(from, 1);
      arr.splice(to, 0, it);
      return arr.map((c, i) => ({ ...c, order: i }));
    });
    setDirty(true);
  };
  const setColumnWidth = (key, width) => {
    setColumns((cs) => cs.map((c) => c.key === key ? { ...c, width } : c));
    setDirty(true);
  };
  // Iter Feb2026 · Reset every column's width back to the codebase
  // default supplied at mount time. Used by the "Fit" button's 4th
  // cycle ("reset to saved widths").
  const resetColumnWidths = () => {
    setColumns((cs) => cs.map((c) => {
      const orig = (defaultColumns || []).find((d) => d.key === c.key);
      return orig ? { ...c, width: orig.width } : c;
    }));
    setDirty(true);
  };
  const toggleSort = (key, opts = {}) => {
    // R267i · Feb 2026 — Multi-sort support. Passing `additive:true`
    // (typically bound to Shift-click on a column header) appends
    // this column to the sort stack instead of clearing the others.
    // The full stack persists via `currentLayout.sort`, so saved
    // views / presets automatically carry the multi-column order.
    setSort((prev) => {
      const cur = prev.find((s) => s.key === key);
      const others = prev.filter((s) => s.key !== key);
      const nextDir = !cur ? "asc" : cur.dir === "asc" ? "desc" : null;
      const baseline = opts.additive ? others : [];
      if (nextDir === null) return baseline;
      return [...baseline, { key, dir: nextDir }];
    });
    setDirty(true);
  };
  const setFilterValue = (key, op, value) => {
    setFilters((prev) => {
      const rest = prev.filter((f) => f.key !== key);
      if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) return rest;
      return [...rest, { key, op, value }];
    });
    setDirty(true);
  };
  const clearFilters = () => { setFilters([]); setDirty(true); };
  const applyView = (view) => {
    applyLayout(view.config);
    setActiveViewId(view.id);
    setBaseViewId(view.id);
  };

  // Saved view ops
  const saveNewView = async ({ name, description, scope }) => {
    if (!name?.trim()) { toast.error("Name required"); return null; }
    if (!activeWorkspaceId) { toast.error("No active workspace"); return null; }
    // Oct 2026 · Defensive sanitation. Legacy saved views used to drop
    // `op` on filter entries which then fails backend Pydantic
    // validation (422 "op · field required") when the user re-saves.
    // Default to `eq` and strip any entry without a key.
    const safeConfig = (() => {
      const cfg = { ...currentLayout };
      cfg.filters = (Array.isArray(cfg.filters) ? cfg.filters : [])
        .filter((f) => f && typeof f === "object" && f.key)
        .map((f) => ({ key: f.key, op: f.op || "eq", value: f.value ?? null }));
      cfg.sort = (Array.isArray(cfg.sort) ? cfg.sort : [])
        .filter((s) => s && typeof s === "object" && s.key)
        .map((s) => ({ key: s.key, dir: s.dir === "desc" ? "desc" : "asc" }));
      return cfg;
    })();
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/table-views`, {
        table_key: tableKey, name, description, scope, config: safeConfig,
      });
      toast.success(`View "${data.name}" saved`);
      await loadViews();
      setActiveViewId(data.id);
      setDirty(false);
      return data;
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); return null; }
  };
  const updateView = async (id, patch = {}) => {
    try {
      // Iter · Feb 2026 — ONLY overwrite `config` when the caller
      // explicitly provided one. Previous logic always sent
      // `currentLayout`, which meant an admin renaming another user's
      // view would silently clobber that user's saved layout with the
      // admin's current on-screen table state.
      const body = { ...patch };
      if (body.config === undefined) delete body.config;
      await api.patch(`/table-views/${id}`, body);
      toast.success("View updated");
      setDirty(false);
      await loadViews();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const deleteView = async (id) => {
    if (!confirm("Delete this saved view?")) return;
    try {
      await api.delete(`/table-views/${id}`);
      toast.success("View deleted");
      if (activeViewId === id) setActiveViewId(null);
      await loadViews();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  const setWorkspaceDefault = async (id) => {
    try {
      await api.post(`/table-views/${id}/set-workspace-default`);
      toast.success("Set as workspace default");
      await loadViews();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  const setUserDefault = async (id) => {
    try {
      await api.post(`/table-views/${id}/set-user-default`);
      toast.success("Pinned as your default");
      await loadViews();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  const clearUserDefault = async () => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/tables/${tableKey}/clear-user-default`);
      toast.success("Reset to workspace default");
      await loadViews();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  // Iter · Feb 2026 — Admin view management.
  const listAllViews = useCallback(async () => {
    if (!activeWorkspaceId) return { views: [], role_defaults: {} };
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/table-views`, {
        params: { table_key: tableKey, include_all_users: true },
      });
      return { views: data.views || [], role_defaults: data.role_defaults || {} };
    } catch (e) { toast.error(apiErrorMessage(e, "Load all views failed")); return { views: [], role_defaults: {} }; }
  }, [activeWorkspaceId, tableKey]);
  const cloneViewToUser = async (vid, userId, setAsDefault = false) => {
    try {
      await api.post(`/table-views/${vid}/clone-into-user`, { user_id: userId, set_as_default: setAsDefault });
      toast.success(setAsDefault ? "View cloned and set as their default" : "View cloned into their views");
      await loadViews();
      return true;
    } catch (e) { toast.error(apiErrorMessage(e, "Clone failed")); return false; }
  };
  const cloneViewToRole = async (vid, roleKey, setAsDefault = false) => {
    try {
      const { data } = await api.post(`/table-views/${vid}/clone-into-role`,
        { role_key: roleKey, set_as_default: setAsDefault });
      const parts = [`Cloned to ${data.cloned || 0} user${data.cloned === 1 ? "" : "s"}`];
      if (setAsDefault && data.defaulted) parts.push(`· ${data.defaulted} defaulted`);
      toast.success(parts.join(" "));
      await loadViews();
      return true;
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk clone failed")); return false; }
  };
  const setRoleDefault = async (vid, roleKey) => {
    try {
      await api.post(`/table-views/${vid}/set-role-default`, { role_key: roleKey });
      toast.success(`Set as default for '${roleKey}'`);
      await loadViews();
      return true;
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); return false; }
  };
  const clearRoleDefault = async (roleKey) => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/tables/${tableKey}/clear-role-default`, null, {
        params: { role_key: roleKey },
      });
      toast.success(`Cleared default for '${roleKey}'`);
      await loadViews();
      return true;
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); return false; }
  };
  const resetToDefaults = () => {
    localStorage.removeItem(lsKey(activeWorkspaceId, tableKey, uid));
    applyLayout({
      columns: defaultColumns.map((c, i) => ({
        key: c.key, visible: c.visible !== false, order: i,
        width: c.width || null, frozen: !!c.frozen,
      })),
      sort: defaultLayout.sort || [],
      filters: [],
      group_by: null,
      search: "",
    });
    setActiveViewId(null);
    setBaseViewId(null);
    setDirty(false);
  };

  return {
    // state
    columns, sort, filters, groupBy, search, wrapMode, currentLayout, dirty,
    views, activeViewId, baseViewId, wsDefaultId, userDefaultId, isAdmin, viewsLoaded,
    // layout ops
    setColumns, setColumnVisibility, toggleFrozen, reorderColumns, setColumnWidth, resetColumnWidths,
    toggleSort, setSort, setFilterValue, setFilters, clearFilters, setSearch, setGroupBy, setWrapMode,
    resetToDefaults,
    // view ops
    applyView, saveNewView, updateView, deleteView,
    setWorkspaceDefault, setUserDefault, clearUserDefault,
    // Iter · Feb 2026 — Admin view management ops
    listAllViews, cloneViewToUser, cloneViewToRole, setRoleDefault, clearRoleDefault,
    reloadViews: loadViews,
  };
}
